"use client";
import { useState } from "react";

import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";

import Image from "next/image";
import Link from "next/link";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import { FieldController } from "../forms/FieldController";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { addContextToUrl } from "@/utils/contextUrl";
import { viruses } from "@prisma/client";

interface FormLogin {
  email: string;
  password: string;
}

export default function Login() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<boolean>(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<FormLogin>({
    mode: "all",
    defaultValues: {},
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const onSubmit = async (data: any) => {
    try {
      const contextParam = searchParams.get("context");
      
      await signIn("credentials", {
        email: data.email,
        password: data.password,
      });
      
      let redirectUrl = "/account";
      if (contextParam && Object.values(viruses).includes(contextParam as viruses)) {
        redirectUrl = addContextToUrl("/account", contextParam as viruses);
      }
      
      window.location.replace(redirectUrl);
    } catch (error) {
      setLoginError(true);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "center",
        width: "864px",
        height: "100vh",
        mt: 4,
        gap: 2,
      }}
    >
      <Link href="/">
        <Image
          alt="Arbogen logo"
          src="/assets/logo/Arbogen.png"
          width={158}
          height={54}
        />
      </Link>
      <Typography
        variant="h6"
        sx={theme => ({
          color: theme.palette.primary.main
        })}
      >
        Welcome to Arbogen
      </Typography>
      <Box>
        <Typography sx={{
          marginBottom: "2"
        }}>
          The Arbogen platform is your go-to destination for quality-certified
          data and tools for arbovirus sequence mining and analysis
        </Typography>
        <Typography sx={{
          marginBottom: "2"
        }}>
          Whether you are a new user or a long-time member, we are delighted to
          welcome you.
        </Typography>
      </Box>
      <Paper
        sx={{
          width: "688px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 2,
          gap: 2,
        }}
        elevation={2}
      >
        <Typography
          variant="h6"
          sx={theme => ({
            color: theme.palette.secondary.main
          })}
        >
          Login
        </Typography>
        <Box
          component="form"
          sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}
        >
          {loginError && (
            <Alert sx={{ width: "100%", mb: 4 }} severity="error">
              {"We couldn't authenticate you"}
            </Alert>
          )}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: 2,
              flexWrap: "nowrap",
            }}
          >
            <FieldController
              control={control}
              sx={{ width: "49%" }}
              rules={{ required: "Email is required." }}
              id="email"
              label="Email address"
              name="email"
              autoComplete="email"
            />
            <FieldController
              control={control}
              sx={{ width: "49%" }}
              rules={{ required: "Password is required." }}
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="currentPassword"
              formHelperTextProps={{ sx: { ml: 0 } }}
              inputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              height: "32px",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <Button
              variant="text"
              onClick={() => router.push("/forgotPassword")}
            >
              Forgot password?
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button variant="text" onClick={() => router.push("/signup")}>
            Create an account
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            Login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
